"use client";
import { useEffect } from "@tiendanube/nube-sdk-react-runtime";

/**
 * Number of active locks. The page styles are only touched by the first lock
 * and restored by the last unlock, so overlapping locks can't restore the page
 * scroll while another one is still active.
 */
let lockCount = 0;
let restore: (() => void) | null = null;

function lock() {
	lockCount += 1;
	if (lockCount > 1) return;

	const html = document.documentElement;
	const body = document.body;
	const prev = {
		htmlOverflow: html.style.overflow,
		bodyOverflow: body.style.overflow,
		bodyPaddingRight: body.style.paddingRight,
	};

	// Hiding the scrollbar widens the layout; pad it back so the page behind
	// the overlay doesn't shift sideways on desktop.
	const scrollbarWidth = window.innerWidth - html.clientWidth;
	if (scrollbarWidth > 0) {
		const current = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;
		body.style.paddingRight = `${current + scrollbarWidth}px`;
	}

	// Both elements: depending on the theme, the scrolling element is either.
	html.style.overflow = "hidden";
	body.style.overflow = "hidden";

	restore = () => {
		html.style.overflow = prev.htmlOverflow;
		body.style.overflow = prev.bodyOverflow;
		body.style.paddingRight = prev.bodyPaddingRight;
	};
}

function unlock() {
	if (lockCount === 0) return;
	lockCount -= 1;
	if (lockCount > 0) return;

	restore?.();
	restore = null;
}

/**
 * Locks the storefront page scroll while `active` is true, so scroll gestures
 * inside the modal don't move the page behind it.
 */
export function useBodyScrollLock(active: boolean) {
	useEffect(() => {
		if (!active || typeof document === "undefined") return;

		lock();
		return unlock;
	}, [active]);
}
